import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
    AlertCircle,
    Loader2,
    Fence,
    Grid3x3,
    Layers,
    Construction,
    Trees,
    Home as HomeIcon,
    HelpCircle,
} from "lucide-react";
import { BrandButton } from "@/components/ui/brand-button";
import {
    SERVICE_OPTIONS,
    PROPERTY_TYPES,
    PROJECT_SIZES,
    TIMELINES,
    BEST_TIMES,
    REFERRAL_SOURCES,
    NUM_SITES,
    ROLES,
    QUOTE_API_PATH,
} from "@/lib/quote-options";
import { COMPANY } from "@/lib/constants";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}${QUOTE_API_PATH}`;

const SERVICE_ICONS = {
    Fence,
    Grid3x3,
    Layers,
    Construction,
    Trees,
    Home: HomeIcon,
    HelpCircle,
};

const PHONE_RE = /^\d{10}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const POSTAL_RE = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

const INITIAL = {
    services: [],
    property_type: "",
    project_size: "",
    description: "",
    timeline: "",
    city: "",
    postal_code: "",
    full_name: "",
    phone: "",
    email: "",
    best_time: "",
    referral_source: "",
    num_sites: "",
    role: "",
};

const buildInitial = ({ initialServices, initialCity, validServiceIds }) => ({
    ...INITIAL,
    services: Array.isArray(initialServices)
        ? initialServices.filter((s) => validServiceIds.has(s))
        : [],
    city: typeof initialCity === "string" ? initialCity : "",
});

const STEPS = [
    { num: 1, label: "Service" },
    { num: 2, label: "Project" },
    { num: 3, label: "Timeline" },
    { num: 4, label: "Contact" },
];

const Eyebrow = ({ children }) => (
    <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-2">
        {children}
    </p>
);

const StepTitle = ({ children }) => (
    <h3 className="font-display text-3xl sm:text-4xl tracking-wide text-brand-dark leading-tight">
        {children}
    </h3>
);

const Label = ({ children, required }) => (
    <span className="block text-sm font-semibold text-brand-dark mb-2">
        {children}
        {required && <span className="text-brand-coral ml-1">*</span>}
    </span>
);

const ErrorText = ({ children }) =>
    children ? (
        <p className="mt-1 flex items-center gap-1.5 text-xs text-brand-coral">
            <AlertCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
            {children}
        </p>
    ) : null;

const inputBase =
    "block w-full bg-white border-2 border-brand-dark/15 px-4 py-3 text-base text-brand-dark placeholder:text-brand-gray focus:border-brand-cyan focus:outline-none focus:ring-0 transition-colors duration-150";

const ProgressBar = ({ step }) => (
    <div className="mb-10" data-testid="quote-progress">
        <div className="flex items-center justify-between mb-3">
            <span className="text-xs uppercase tracking-wider font-semibold text-brand-gray">
                Step {step} of {STEPS.length}
            </span>
            <span className="text-xs uppercase tracking-wider font-semibold text-brand-dark">
                {STEPS[step - 1].label}
            </span>
        </div>
        <div className="grid grid-cols-4 gap-1.5">
            {STEPS.map((s) => (
                <div
                    key={s.num}
                    className={cn(
                        "h-1.5 transition-colors duration-300",
                        s.num <= step ? "bg-brand-cyan" : "bg-brand-dark/10",
                    )}
                    data-testid={`quote-progress-segment-${s.num}`}
                />
            ))}
        </div>
    </div>
);

const ServiceTile = ({ option, checked, onToggle }) => {
    const Icon = SERVICE_ICONS[option.icon] || HelpCircle;
    return (
        <button
            type="button"
            onClick={() => onToggle(option.id)}
            className={cn(
                "group relative flex flex-col items-start gap-3 p-5 text-left border-2 transition-all duration-200",
                checked
                    ? "border-brand-cyan bg-brand-cyan/5 shadow-sm"
                    : "border-brand-dark/10 bg-white hover:border-brand-dark/30",
            )}
            aria-pressed={checked}
            data-testid={`quote-service-${option.id}`}
        >
            <span
                className={cn(
                    "flex h-10 w-10 items-center justify-center transition-colors",
                    checked ? "bg-brand-cyan text-white" : "bg-brand-offwhite text-brand-dark",
                )}
            >
                <Icon className="h-5 w-5" strokeWidth={2.25} />
            </span>
            <span className="font-semibold text-sm text-brand-dark leading-tight">
                {option.label}
            </span>
            <span
                className={cn(
                    "absolute top-3 right-3 h-5 w-5 border-2 flex items-center justify-center transition-colors",
                    checked
                        ? "border-brand-cyan bg-brand-cyan text-white"
                        : "border-brand-dark/20 bg-white",
                )}
            >
                {checked && <CheckCircle2 className="h-4 w-4" strokeWidth={2.5} />}
            </span>
        </button>
    );
};

const RadioCard = ({ id, label, sub, checked, onSelect, testid }) => (
    <button
        type="button"
        onClick={() => onSelect(id)}
        className={cn(
            "flex flex-col items-start gap-1 p-4 text-left border-2 transition-all duration-200",
            checked
                ? "border-brand-cyan bg-brand-cyan/5"
                : "border-brand-dark/10 bg-white hover:border-brand-dark/30",
        )}
        aria-pressed={checked}
        data-testid={testid}
    >
        <span className="font-semibold text-sm text-brand-dark">{label}</span>
        {sub && <span className="text-xs text-brand-gray">{sub}</span>}
    </button>
);

const QuoteForm = ({
    compact = false,
    sourcePage,
    initialServices,
    initialCity,
    utmSource = "",
    utmContent = "",
    chatSession = "",
    track = "residential",
    serviceOptions = SERVICE_OPTIONS,
    propertyTypes = PROPERTY_TYPES,
    isB2B = false,
}) => {
    const validServiceIds = useMemo(
        () => new Set(serviceOptions.map((s) => s.id)),
        [serviceOptions],
    );
    const [step, setStep] = useState(1);
    const [data, setData] = useState(() =>
        buildInitial({ initialServices, initialCity, validServiceIds }),
    );
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [serverError, setServerError] = useState("");

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [step, submitted]);

    const update = (patch) => {
        setData((prev) => ({ ...prev, ...patch }));
        setErrors((prev) => {
            const next = { ...prev };
            Object.keys(patch).forEach((k) => delete next[k]);
            return next;
        });
    };

    const toggleService = (id) => {
        const next = data.services.includes(id)
            ? data.services.filter((s) => s !== id)
            : [...data.services, id];
        update({ services: next });
    };

    const validateStep = (s) => {
        const e = {};
        if (s === 1) {
            if (data.services.length === 0) e.services = "Please select at least one service.";
        }
        if (s === 2) {
            if (!data.property_type) e.property_type = "Please choose a property type.";
            if (isB2B && !data.num_sites) e.num_sites = "Please choose the number of sites.";
            if (isB2B && !data.role) e.role = "Please choose your role.";
            if (!data.project_size) e.project_size = "Please choose an approximate size.";
        }
        if (s === 3) {
            if (!data.timeline) e.timeline = "Please choose a timeline.";
            if (!data.city.trim()) e.city = "City is required.";
            if (data.postal_code && !POSTAL_RE.test(data.postal_code.trim())) {
                e.postal_code = "Use a Canadian postal code (e.g., L6X 1A1).";
            }
        }
        if (s === 4) {
            if (!data.full_name.trim()) e.full_name = "Your full name is required.";
            const phoneDigits = data.phone.replace(/\D/g, "");
            if (!PHONE_RE.test(phoneDigits)) {
                e.phone = "Enter a 10-digit North American phone number.";
            }
            if (!EMAIL_RE.test(data.email.trim())) {
                e.email = "Enter a valid email address.";
            }
            if (!data.best_time) e.best_time = "Please choose a contact window.";
        }
        return e;
    };

    const handleNext = () => {
        const e = validateStep(step);
        if (Object.keys(e).length > 0) {
            setErrors(e);
            return;
        }
        setStep((s) => Math.min(s + 1, STEPS.length));
    };

    const handleBack = () => {
        setErrors({});
        setStep((s) => Math.max(s - 1, 1));
    };

    const handleSubmit = async () => {
        const e = validateStep(4);
        if (Object.keys(e).length > 0) {
            setErrors(e);
            return;
        }
        setSubmitting(true);
        setServerError("");
        try {
            const payload = {
                ...data,
                track,
                phone: data.phone.replace(/\D/g, ""),
                source_page:
                    sourcePage ||
                    (typeof window !== "undefined" ? window.location.pathname : ""),
                utm_source: utmSource || "",
                utm_content: utmContent || "",
                chat_session: chatSession || "",
            };
            const res = await axios.post(API_URL, payload, {
                headers: { "Content-Type": "application/json" },
            });
            if (res.status >= 200 && res.status < 300) {
                setSubmitted(true);
                if (typeof window !== "undefined" && typeof window.gtag === "function") {
                    window.gtag("event", "generate_lead", {
                        source_page: payload.source_page,
                        track,
                    });
                }
            } else {
                setServerError("Something went wrong. Please call us or try again.");
            }
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (Array.isArray(detail) && detail.length > 0) {
                setServerError(detail.map((d) => d.msg || "Invalid field").join(" • "));
            } else if (typeof detail === "string") {
                setServerError(detail);
            } else {
                setServerError("Could not submit your request. Please call us at " + COMPANY.phone + ".");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const canNext = useMemo(() => {
        if (step === 1) return data.services.length > 0;
        if (step === 2)
            return (
                data.property_type &&
                data.project_size &&
                (!isB2B || (data.num_sites && data.role))
            );
        if (step === 3) return data.timeline && data.city.trim().length > 0;
        return true;
    }, [step, data, isB2B]);

    if (submitted) {
        return (
            <div
                className={cn(
                    "bg-white border-2 border-brand-cyan/30 p-8 md:p-12 text-center",
                    !compact && "shadow-sm",
                )}
                data-testid="quote-success"
            >
                <span className="inline-flex h-16 w-16 items-center justify-center bg-brand-cyan text-white mb-5">
                    <CheckCircle2 className="h-9 w-9" strokeWidth={2.25} />
                </span>
                <h3 className="font-display text-4xl sm:text-5xl tracking-wide text-brand-dark mb-3">
                    Thanks — We&apos;ve Got It.
                </h3>
                <p className="text-base text-brand-dark/70 max-w-md mx-auto">
                    A Reformity team member will reach out within 24 hours to confirm
                    details and schedule your free on-site quote.
                </p>
                <p className="mt-6 text-sm text-brand-gray">
                    Need to talk sooner?{" "}
                    <a
                        href={COMPANY.phoneHref}
                        className="text-brand-cyan font-semibold hover:underline"
                    >
                        Call {COMPANY.phone}
                    </a>
                </p>
            </div>
        );
    }

    return (
        <div
            className={cn(
                "bg-white border border-brand-dark/10 p-6 sm:p-8 md:p-10",
                !compact && "shadow-sm",
            )}
            data-testid="quote-form"
        >
            <ProgressBar step={step} />

            {step === 1 && (
                <div className="space-y-6" data-testid="quote-step-1">
                    <div>
                        <Eyebrow>Step 1</Eyebrow>
                        <StepTitle>What can we build for you?</StepTitle>
                        <p className="mt-2 text-sm text-brand-dark/70">
                            Pick one or more — you can always add details later.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        {serviceOptions.map((opt) => (
                            <ServiceTile
                                key={opt.id}
                                option={opt}
                                checked={data.services.includes(opt.id)}
                                onToggle={toggleService}
                            />
                        ))}
                    </div>
                    <ErrorText>{errors.services}</ErrorText>
                </div>
            )}

            {step === 2 && (
                <div className="space-y-8" data-testid="quote-step-2">
                    <div>
                        <Eyebrow>Step 2</Eyebrow>
                        <StepTitle>Tell us about the project.</StepTitle>
                    </div>

                    <div>
                        <Label required>Property type</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-2xl">
                            {propertyTypes.map((pt) => (
                                <RadioCard
                                    key={pt.id}
                                    id={pt.id}
                                    label={pt.label}
                                    checked={data.property_type === pt.id}
                                    onSelect={(id) => update({ property_type: id })}
                                    testid={`quote-property-${pt.id}`}
                                />
                            ))}
                        </div>
                        <ErrorText>{errors.property_type}</ErrorText>
                    </div>

                    {isB2B && (
                        <>
                            <div>
                                <Label required>Number of sites / properties</Label>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl">
                                    {NUM_SITES.map((n) => (
                                        <RadioCard
                                            key={n.id}
                                            id={n.id}
                                            label={n.label}
                                            checked={data.num_sites === n.id}
                                            onSelect={(id) => update({ num_sites: id })}
                                            testid={`quote-numsites-${n.id}`}
                                        />
                                    ))}
                                </div>
                                <ErrorText>{errors.num_sites}</ErrorText>
                            </div>

                            <div>
                                <Label required>Your role</Label>
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                                    {ROLES.map((r) => (
                                        <RadioCard
                                            key={r.id}
                                            id={r.id}
                                            label={r.label}
                                            checked={data.role === r.id}
                                            onSelect={(id) => update({ role: id })}
                                            testid={`quote-role-${r.id}`}
                                        />
                                    ))}
                                </div>
                                <ErrorText>{errors.role}</ErrorText>
                            </div>
                        </>
                    )}

                    <div>
                        <Label required>Approximate project size</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                            {PROJECT_SIZES.map((ps) => (
                                <RadioCard
                                    key={ps.id}
                                    id={ps.id}
                                    label={ps.label}
                                    sub={ps.sub}
                                    checked={data.project_size === ps.id}
                                    onSelect={(id) => update({ project_size: id })}
                                    testid={`quote-size-${ps.id}`}
                                />
                            ))}
                        </div>
                        <ErrorText>{errors.project_size}</ErrorText>
                    </div>

                    <div>
                        <Label>Tell us a bit more (optional)</Label>
                        <textarea
                            className={cn(inputBase, "min-h-[120px] resize-y")}
                            value={data.description}
                            onChange={(e) => update({ description: e.target.value })}
                            maxLength={1000}
                            placeholder="Size, materials, anything specific…"
                            data-testid="quote-description"
                        />
                        <p className="mt-1 text-xs text-brand-gray text-right">
                            {data.description.length}/1000
                        </p>
                    </div>
                </div>
            )}

            {step === 3 && (
                <div className="space-y-8" data-testid="quote-step-3">
                    <div>
                        <Eyebrow>Step 3</Eyebrow>
                        <StepTitle>When &amp; where?</StepTitle>
                    </div>

                    <div>
                        <Label required>When would you like to start?</Label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                            {TIMELINES.map((t) => (
                                <RadioCard
                                    key={t.id}
                                    id={t.id}
                                    label={t.label}
                                    checked={data.timeline === t.id}
                                    onSelect={(id) => update({ timeline: id })}
                                    testid={`quote-timeline-${t.id}`}
                                />
                            ))}
                        </div>
                        <ErrorText>{errors.timeline}</ErrorText>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                            <Label required>City</Label>
                            <input
                                type="text"
                                className={inputBase}
                                value={data.city}
                                onChange={(e) => update({ city: e.target.value })}
                                placeholder="Brampton"
                                data-testid="quote-city"
                            />
                            <ErrorText>{errors.city}</ErrorText>
                        </div>
                        <div>
                            <Label>Postal code (optional)</Label>
                            <input
                                type="text"
                                className={inputBase}
                                value={data.postal_code}
                                onChange={(e) =>
                                    update({ postal_code: e.target.value.toUpperCase() })
                                }
                                placeholder="L6X 1A1"
                                maxLength={7}
                                data-testid="quote-postal"
                            />
                            <ErrorText>{errors.postal_code}</ErrorText>
                        </div>
                    </div>
                </div>
            )}

            {step === 4 && (
                <div className="space-y-7" data-testid="quote-step-4">
                    <div>
                        <Eyebrow>Step 4</Eyebrow>
                        <StepTitle>Who should we contact?</StepTitle>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                            <Label required>Full name</Label>
                            <input
                                type="text"
                                className={inputBase}
                                value={data.full_name}
                                onChange={(e) => update({ full_name: e.target.value })}
                                placeholder="Jane Doe"
                                data-testid="quote-name"
                                autoComplete="name"
                            />
                            <ErrorText>{errors.full_name}</ErrorText>
                        </div>
                        <div>
                            <Label required>Phone</Label>
                            <input
                                type="tel"
                                className={inputBase}
                                value={data.phone}
                                onChange={(e) => update({ phone: e.target.value })}
                                placeholder="(416) 555-1234"
                                data-testid="quote-phone"
                                autoComplete="tel"
                            />
                            <ErrorText>{errors.phone}</ErrorText>
                        </div>
                    </div>

                    <div>
                        <Label required>Email</Label>
                        <input
                            type="email"
                            className={inputBase}
                            value={data.email}
                            onChange={(e) => update({ email: e.target.value })}
                            placeholder="jane@email.com"
                            data-testid="quote-email"
                            autoComplete="email"
                        />
                        <ErrorText>{errors.email}</ErrorText>
                    </div>

                    <div>
                        <Label required>Best time to reach you</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {BEST_TIMES.map((bt) => (
                                <RadioCard
                                    key={bt.id}
                                    id={bt.id}
                                    label={bt.label}
                                    checked={data.best_time === bt.id}
                                    onSelect={(id) => update({ best_time: id })}
                                    testid={`quote-besttime-${bt.id}`}
                                />
                            ))}
                        </div>
                        <ErrorText>{errors.best_time}</ErrorText>
                    </div>

                    <div>
                        <Label>How did you hear about us? (optional)</Label>
                        <select
                            className={cn(inputBase, "appearance-none cursor-pointer")}
                            value={data.referral_source}
                            onChange={(e) => update({ referral_source: e.target.value })}
                            data-testid="quote-referral"
                        >
                            <option value="">— Select an option —</option>
                            {REFERRAL_SOURCES.map((src) => (
                                <option key={src} value={src}>
                                    {src}
                                </option>
                            ))}
                        </select>
                    </div>

                    {serverError && (
                        <div className="border-l-4 border-brand-coral bg-brand-coral/10 p-4 text-sm text-brand-dark" data-testid="quote-server-error">
                            <p className="font-semibold flex items-center gap-2">
                                <AlertCircle className="h-4 w-4 text-brand-coral" strokeWidth={2.5} />
                                Submission failed
                            </p>
                            <p className="mt-1 text-brand-dark/80">{serverError}</p>
                        </div>
                    )}
                </div>
            )}

            {/* Footer nav */}
            <div className="mt-10 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-brand-dark/10 pt-6">
                <BrandButton
                    variant="outline"
                    tone="onLight"
                    size="md"
                    onClick={handleBack}
                    disabled={step === 1 || submitting}
                    className={cn(step === 1 && "invisible")}
                    data-testid="quote-back"
                >
                    <ArrowLeft className="h-4 w-4" strokeWidth={2.25} />
                    Back
                </BrandButton>

                {step < STEPS.length ? (
                    <BrandButton
                        variant="primary"
                        size="md"
                        onClick={handleNext}
                        disabled={!canNext}
                        data-testid="quote-next"
                    >
                        Continue
                        <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                    </BrandButton>
                ) : (
                    <BrandButton
                        variant="primary"
                        size="md"
                        onClick={handleSubmit}
                        disabled={submitting}
                        data-testid="quote-submit"
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.25} />
                                Sending…
                            </>
                        ) : (
                            <>
                                Submit Request
                                <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                            </>
                        )}
                    </BrandButton>
                )}
            </div>

            {step === STEPS.length && (
                <p
                    className="mt-4 text-xs leading-relaxed text-brand-dark/65"
                    data-testid="quote-privacy-note"
                >
                    By submitting, you agree we can contact you about this
                    request. We use your details only to quote and complete your
                    project — see our{" "}
                    <Link
                        to="/privacy"
                        className="font-semibold text-brand-coral hover:underline"
                    >
                        Privacy Policy
                    </Link>
                    .
                </p>
            )}
        </div>
    );
};

export default QuoteForm;
